import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ObjectFieldTemplateProps, ArrayFieldTemplateProps } from '@rjsf/utils'
import RjsfForm from '@rjsf/core'
import validator from '@rjsf/validator-ajv8'
import { Form } from 'react-bootstrap'
import { Button } from '../Button/Button'
import { AgendaSmartSuggestionsWidget } from './AgendaSmartSuggestionsWidget'
import { DiscussionTopicsWidget } from './DiscussionTopicsWidget/DiscussionTopicsWidget'

const ObjectFieldTemplate: React.FC<ObjectFieldTemplateProps> = ({ properties }) => {
  return (
    <div>
      {properties.map((prop) => {
        const propertiesContent = React.cloneElement(prop.content, {
          ...prop.content.props,
          name: undefined,
          schema: {
            ...prop.content.props.schema,
            title: undefined,
            description: undefined,
          },
        })

        const iconClassName = prop.content.props.schema?.icon

        return (
          <div key={prop.name} className='d-flex p-2 gap-3'>
            {iconClassName ? (
              <div className='flex-shrink-0 d-flex align-items-start pt-1 fs-5'>
                <i className={iconClassName} />
              </div>
            ) : null}
            <div className='flex-grow-1'>
              {prop.content.props.schema?.title ? (
                <div className='paragraph-medium bold pb-1'>{prop.content.props.schema.title}</div>
              ) : (
                <div className='paragraph-medium bold pb-1'>{prop.content.props.name}</div>
              )}
              {prop.content.props.schema?.description && <div className='paragraph-small text-muted pb-2'>{prop.content.props.schema.description}</div>}
              {propertiesContent}
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface AddFixedItemInputProps {
  onAdd: (text: string) => void
  onCancel: () => void
  disabled?: boolean
}

const AddFixedItemInput: React.FC<AddFixedItemInputProps> = ({ onAdd, onCancel, disabled }) => {
  const [text, setText] = useState('')

  const handleAdd = () => {
    if (!text.trim()) {
      return
    }

    onAdd(text.trim())
    setText('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
    if (e.key === 'Escape') {
      onCancel()
    }
  }

  return (
    <div className='d-flex align-items-start gap-2'>
      <div className='flex-grow-1'>
        <input
          type='text'
          className='form-control'
          placeholder='Enter fixed topic'
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoFocus
        />
      </div>

      <Button type='button' variant='borderless' className='ps-2 mt-2' aria-label='Add item' onClick={handleAdd} disabled={disabled || !text.trim()}>
        <i className='bi bi-check fs-6' />
      </Button>
      <Button type='button' variant='borderless' onClick={onCancel} disabled={disabled} className='ps-0 mt-2' aria-label={`Cancel input`}>
        <i className='bi bi-x fs-6' />
      </Button>
    </div>
  )
}

const FixedAgendaItemsArrayTemplate: React.FC<ArrayFieldTemplateProps> = (props) => {
  const { items, canAdd, disabled, readonly, formContext } = props
  const [showInput, setShowInput] = React.useState(false)

  const handleAdd = (text: string) => {
    formContext?.onAddFixedItem?.(text)
  }

  return (
    <div className='mb-1'>
      {items && items.length > 0 ? (
        <div className='d-flex flex-column gap-2 mb-2'>
          {items.map((item) => (
            <div key={item.key} className='d-flex align-items-start gap-2'>
              <div className='flex-grow-1'>{item.children}</div>
              {item.hasRemove && (
                <Button
                  type='button'
                  variant='borderless'
                  onClick={item.onDropIndexClick(item.index)}
                  disabled={disabled || readonly}
                  className='ps-2 mt-2'
                  aria-label={`Remove item ${item.index + 1}`}
                >
                  <i className='bi bi-trash fs-6' />
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : null}
      {showInput ? <AddFixedItemInput onAdd={handleAdd} onCancel={() => setShowInput(false)} disabled={disabled || readonly} /> : null}
      {canAdd && !showInput ? (
        <div className='zocks-mt-1.25'>
          <Button
            type='button'
            variant='link'
            className='text-decoration-none p-0 zocks-text-primary-dark paragraph-small'
            onClick={() => setShowInput(true)}
            disabled={disabled || readonly}
          >
            <i className='bi bi-plus-lg zocks-text-primary' />
            <span className='ms-1 zocks-text-primary'>Add item</span>
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export interface InputParameterSchemaConfigFormProps {
  inputParametersSchema: { [key: string]: any } | undefined
  inputParameters: { [key: string]: any }
  showCustomizationSetting: boolean
  customized: boolean
  onChange: (newInputParameters: { [key: string]: any }) => void
  onChangeCustomized?: () => void
  disabled: boolean
}

export const InputParameterConfigForm: React.FC<InputParameterSchemaConfigFormProps> = ({
  inputParametersSchema,
  inputParameters,
  showCustomizationSetting,
  customized,
  onChange,
  onChangeCustomized,
  disabled,
}) => {
  const inputParametersRef = useRef(inputParameters)
  useEffect(() => {
    inputParametersRef.current = inputParameters
  }, [inputParameters])

  const handleAddFixedItem = useCallback(
    (text: string) => {
      const current = inputParametersRef.current
      const currentFixed = current?.fixed ?? []
      onChange({
        ...current,
        fixed: [...currentFixed, text],
      })
    },
    [onChange],
  )

  if (!inputParametersSchema) {
    return null
  }

  const disabledByCustomization = showCustomizationSetting && !customized

  const customWidgets = {
    AgendaSmartSuggestionsWidget: AgendaSmartSuggestionsWidget,
    DiscussionTopicsWidget: DiscussionTopicsWidget,
  }

  const smartSuggestionOptions = inputParametersSchema?.properties?.smartSuggestions?.options ?? []

  return (
    <>
      {showCustomizationSetting ? (
        <div className='d-flex justify-content-end'>
          <Form.Check className='form-check' checked={customized} onChange={onChangeCustomized} label='Customize' disabled={disabled} />
        </div>
      ) : null}
      <RjsfForm
        disabled={disabled || disabledByCustomization}
        schema={inputParametersSchema}
        widgets={customWidgets}
        formContext={{ onAddFixedItem: handleAddFixedItem }}
        uiSchema={{
          'ui:submitButtonOptions': {
            norender: true,
          },
          'ui:ObjectFieldTemplate': ObjectFieldTemplate,
          template: {
            'ui:widget': 'textarea',
          },
          fixed: {
            'ui:ArrayFieldTemplate': FixedAgendaItemsArrayTemplate,
            'ui:options': {
              orderable: false,
            },
          },
          smartSuggestions: {
            'ui:widget': AgendaSmartSuggestionsWidget,
            'ui:options': {
              smartSuggestionOptions,
            },
          },
          customInstructions: {
            'ui:widget': 'textarea',
            'ui:options': {
              rows: 5,
            },
          },
          discussionTopics: {
            'ui:widget': DiscussionTopicsWidget,
          },
        }}
        validator={validator}
        formData={inputParameters}
        liveValidate={true}
        onChange={(e) => onChange(e.formData)}
        showErrorList={false}
      />
    </>
  )
}
